
import React, { useState } from 'react';

const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://formsubmit.co/ajax/growlocal25@gmail.com", {
        method: "POST",
        headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error("Failed to send message.");
      }
    } catch (err) {
      console.error("Submission error:", err);
      setError("Something went wrong. Please try again or email us directly at growlocal25@gmail.com.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-24">
      <div className="modern-card overflow-hidden flex flex-col lg:flex-row">
        <div className="lg:w-5/12 bg-emerald-900 p-16 text-white space-y-12 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-emerald-500/20 blur-[100px] rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          
          <div className="relative space-y-6">
            <h2 className="text-6xl font-black serif-heading leading-none">Connect with us.</h2>
            <p className="text-xl text-emerald-100/60 leading-relaxed font-medium">
              Join the conversation around sustainable education and urban food production.
            </p>
          </div>
          
          <div className="space-y-8 relative">
            <div className="group flex items-center gap-6">
              <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center text-2xl shadow-lg border border-emerald-700 group-hover:bg-emerald-700 transition-colors">📧</div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">General Inquiries</p>
                <p className="text-lg font-bold">growlocal25@gmail.com</p>
              </div>
            </div>
            <div className="group flex items-center gap-6">
              <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center text-2xl shadow-lg border border-emerald-700 group-hover:bg-emerald-700 transition-colors">📍</div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Headquarters</p>
                <p className="text-lg font-bold">Scarborough, ON</p>
              </div>
            </div>
          </div>

          <div className="pt-12 relative">
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/growlocal.ja/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center hover:bg-emerald-500 transition-all cursor-pointer font-black text-[10px] tracking-tighter" 
                title="Instagram"
              >
                IG
              </a>
              <a 
                href="https://www.tiktok.com/@ja_team25" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center hover:bg-emerald-500 transition-all cursor-pointer font-black text-[10px] tracking-tighter" 
                title="TikTok"
              >
                TK
              </a>
            </div>
          </div>
        </div>

        <div className="flex-1 p-16 md:p-24">
          {submitted ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-8 animate-in fade-in zoom-in">
              <div className="w-32 h-32 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center text-6xl shadow-inner">🌱</div>
              <div className="space-y-2">
                <h3 className="text-4xl font-black text-emerald-900 serif-heading">Inquiry Received.</h3>
                <p className="text-lg text-emerald-900/50 font-medium">Your message has been sent to growlocal25@gmail.com. A JA representative will contact you within 24 business hours.</p>
              </div>
              <button 
                onClick={() => setSubmitted(false)}
                className="text-emerald-600 font-black hover:underline underline-offset-8"
              >
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-bold animate-in slide-in-from-top-2">
                  {error}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-emerald-900/30 uppercase tracking-widest px-1">Full Name</label>
                  <input 
                    required
                    name="name"
                    type="text" 
                    placeholder="Jane Doe"
                    className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl px-6 py-4 text-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all outline-none font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-emerald-900/30 uppercase tracking-widest px-1">Email Address</label>
                  <input 
                    required
                    name="email"
                    type="email" 
                    placeholder="jane@organization.com"
                    className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl px-6 py-4 text-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all outline-none font-medium"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-emerald-900/30 uppercase tracking-widest px-1">Message Body</label>
                <textarea 
                  required
                  name="message"
                  rows={6}
                  placeholder="How can GrowLocal assist your organization or classroom?"
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl px-6 py-4 text-lg focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all outline-none resize-none font-medium"
                ></textarea>
              </div>
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`btn-primary w-full !text-xl !py-5 flex items-center justify-center gap-3 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <span className="w-5 h-5 border-4 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Sending...
                  </>
                ) : (
                  'Send Official Inquiry'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
